import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Card, Grid } from '@mui/material';
import { ICandidate, ICandidateRevenue, IJob, IJobRevenue } from 'wisestep-api';
import {
  WsIconMonyBag,
  WsIconChartLine,
  WsIconCoin,
  WsIconCalculator,
  WsColors,
  WsIconText,
  WsTypoColors,
  WsTypoVariants,
} from 'wisestep-base';

import BasicDetailsCard from './BasicDetailsCard';
import CalculationUnitCard from './CalculationUnitCard';
import RevenueDTO from './RevenueDTO';

function resolveUnitTime(revenue: RevenueDTO): string {
  const hourlyValues = ['HOURLY', 'PerHour'];
  const dailyValues = ['DAILY', 'PerDay'];
  const timeSpanFields = [revenue.netMarginTimeSpan, revenue.clientBillRateTimeSpan];
  if (hourlyValues.some((val) => timeSpanFields.includes(val))) {
    return 'hr';
  }
  if (dailyValues.some((val) => timeSpanFields.includes(val))) {
    return 'day';
  }
  return '';
}

const timeSpanMap = {
  PerHour: 'hr',
  HOURLY: 'hr',
  PerDay: 'day',
  DAILY: 'day',
  PerMonth: 'month',
  MONTHLY: 'month',
  PerAnnum: '',
  ONCE: '',
};

export function resolveTimeSpan(timeSpan: string | null): string {
  if (!timeSpan) return '';
  return timeSpanMap[timeSpan] ?? timeSpan;
}

interface IPotentialrevenue {
  jobRevenueInfo?: IJobRevenue;
  candidateRevenueInfo?: ICandidateRevenue;
  jobInfo?: IJob;
  candidateInteractionInfo?: ICandidate;
}

const PotentialRevenue = ({
  jobRevenueInfo,
  candidateRevenueInfo,
  jobInfo,
  candidateInteractionInfo,
}: IPotentialrevenue) => {
  const revenue: RevenueDTO = {
    feeArrangementName:
      jobRevenueInfo?.feeArrangement?.feeArrangementLevel ||
      candidateRevenueInfo?.feeArrangement?.feeArrangementLevel ||
      'Not Available',
    arrangementType:
      jobRevenueInfo?.feeArrangement?.contractType ||
      candidateRevenueInfo?.feeArrangement?.contractType ||
      jobInfo?.jobType ||
      'Not Available',
    numberOfPosition: jobInfo?.numberOfPositions?.toString() ?? '',
    contractDuration: null,
    standardContract:
      (
        candidateRevenueInfo?.revenueCalculatedFor || jobRevenueInfo?.revenueCalculatedFor
      )?.toString() ?? '',
    maxSalary:
      (candidateInteractionInfo?.expectedBaseSalary || jobInfo?.maximumSalary)?.toString() ?? '',
    salaryCurrency:
      candidateInteractionInfo?.expectedSalaryCurrency || jobInfo?.salaryCurrency || '',
    salaryTimeSpan:
      candidateInteractionInfo?.expectedBaseSalaryTimeSpan || jobInfo?.salaryTimeSpan || '',
    potentialRevenueValue: jobRevenueInfo?.potentialRevenue?.toString() ?? '',
    grossRevenue:
      (candidateRevenueInfo?.grossRevenue || jobRevenueInfo?.grossRevenue)?.toString() ?? '',
    netRevenue:
      (candidateRevenueInfo?.netFeeIncome || jobRevenueInfo?.netFeeIncome)?.toString() ?? '',
    clientBillRate:
      (candidateInteractionInfo?.clientBillRate || jobInfo?.clientBillRate)?.toString() ?? '',
    maxPayRate:
      (candidateInteractionInfo?.expectedBaseSalary || jobInfo?.maximumSalary)?.toString() ?? '',
    potrntialNetMargin:
      (candidateRevenueInfo?.netMargin || jobRevenueInfo?.netMargin)?.toString() ?? '',
    potentialRevenueCurrency: jobInfo?.salaryCurrency || jobInfo?.clientBillRateCurrency || '',
    potentialRevenueTimeSpan: jobRevenueInfo?.potentialRevenueTimeSpan ?? '',
    netFeeIncomeTimeSpan:
      candidateRevenueInfo?.netFeeIncomeTimeSpan || jobRevenueInfo?.netFeeIncomeTimeSpan || '',
    grossRevenueTimeSpan:
      candidateRevenueInfo?.grossRevenueTimeSpan || jobRevenueInfo?.grossRevenueTimeSpan || '',
    clientBillRateCurrency:
      candidateInteractionInfo?.clientBillRateCurrency || jobInfo?.clientBillRateCurrency || '',
    netMarginTimeSpan:
      candidateRevenueInfo?.netMarginTimeSpan || jobRevenueInfo?.netMarginTimeSpan || '',
    clientBillRateTimeSpan:
      candidateInteractionInfo?.clientBillRateTimeSpan || jobInfo?.clientBillRateTimeSpan || '',
    jobDuration: jobInfo?.workDuration ?? '',
    jobDurationTimeSpan: jobInfo?.workDurationTimeSpan ?? '',
    payRateExpenses: candidateInteractionInfo?.payRateExpenses?.toString() ?? '',
    payRateExpensesCurrency: candidateInteractionInfo?.payRateExpensesCurrency ?? '',
    payRateExpensesType: candidateInteractionInfo?.payRateExpensesType ?? '',
    offeredPayRate: candidateInteractionInfo?.offeredPayRate?.toString() ?? '',
    offeredPayRateTimeSpan: candidateInteractionInfo?.offeredPayRateTimeSpan ?? '',
    offeredPayRateCurrency: candidateInteractionInfo?.offeredPayRateCurrency ?? '',
  };

  const isJobPermanent = revenue.arrangementType?.toLowerCase() === 'permanent';

  return (
    <Grid container xs={12} spacing={2}>
      {isJobPermanent && jobRevenueInfo && !jobRevenueInfo.feeArrangement && (
        <Grid item xs={12} ml={1} pb={2} sx={{ bgcolor: WsColors.Yellow200, borderRadius: 1 }}>
          <WsIconText
            icon={<InfoOutlinedIcon sx={{ color: WsColors.Yellow600 }} />}
            text="Fee Arrangement is not selected for the job, select Fee Arrangement to calculate the potential Revenue"
            textColor={WsTypoColors.YELLOWDARK}
            textSize={WsTypoVariants.BODY1}
          />
        </Grid>
      )}
      {!isJobPermanent && (!revenue.clientBillRate || revenue.clientBillRate == '0') && (
        <Grid item xs={12} ml={1} pb={2} sx={{ bgcolor: WsColors.Yellow200, borderRadius: 1 }}>
          <WsIconText
            icon={<InfoOutlinedIcon sx={{ color: WsColors.Yellow600 }} />}
            text="Please add client bill rate to calculate the potential revenue"
            textColor={WsTypoColors.YELLOWDARK}
            textSize={WsTypoVariants.BODY1}
          />
        </Grid>
      )}
      <Grid item xs={5} sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <BasicDetailsCard revenue={revenue} />
        </Card>
      </Grid>
      <Grid item xs={7}>
        <Grid container direction="row" spacing={2}>
          {isJobPermanent ? (
            <>
              {revenue.maxPayRate && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.maxPayRate}
                    Icon={WsIconMonyBag}
                    heading="Maximum Salary"
                    currency={revenue.salaryCurrency ?? ''}
                    timeSpan=""
                  />
                </Grid>
              )}
              {revenue.potentialRevenueValue && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.potentialRevenueValue}
                    Icon={WsIconChartLine}
                    heading="Potential Revenue"
                    currency={revenue.potentialRevenueCurrency ?? ''}
                    timeSpan=""
                  />
                </Grid>
              )}
              {revenue.offeredPayRate && revenue.offeredPayRate !== '0' && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.offeredPayRate}
                    Icon={WsIconMonyBag}
                    heading="Offered Salary"
                    currency={revenue.offeredPayRateCurrency ?? ''}
                    timeSpan=""
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              {revenue.clientBillRate && revenue.clientBillRate !== '0' && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.clientBillRate}
                    Icon={WsIconChartLine}
                    heading="Client Bill Rate"
                    currency={revenue.clientBillRateCurrency ?? ''}
                    timeSpan={resolveTimeSpan(revenue.clientBillRateTimeSpan)}
                  />
                </Grid>
              )}
              {revenue.maxSalary &&
                (revenue.offeredPayRate === null || revenue.offeredPayRate === '0') && (
                  <Grid item>
                    <CalculationUnitCard
                      value={revenue.maxSalary}
                      Icon={WsIconMonyBag}
                      heading={candidateRevenueInfo ? 'Submitted Pay Rate' : 'Max Pay Rate'}
                      currency={revenue.salaryCurrency ?? ''}
                      timeSpan={resolveTimeSpan(revenue.salaryTimeSpan)}
                    />
                  </Grid>
                )}
              {revenue.offeredPayRate && revenue.offeredPayRate !== '0' && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.offeredPayRate}
                    Icon={WsIconMonyBag}
                    heading="Offered Pay Rate"
                    currency={revenue.offeredPayRateCurrency ?? ''}
                    timeSpan={resolveTimeSpan(revenue.offeredPayRateTimeSpan)}
                  />
                </Grid>
              )}
              {revenue.payRateExpenses && revenue.payRateExpenses !== '0' && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.payRateExpenses}
                    Icon={WsIconMonyBag}
                    heading="Payrate Expenses"
                    currency={revenue.payRateExpensesCurrency ?? ''}
                    timeSpan={resolveTimeSpan(revenue.payRateExpensesType)}
                  />
                </Grid>
              )}
              {revenue.potrntialNetMargin && revenue.potrntialNetMargin !== '0' && (
                <Grid item>
                  <CalculationUnitCard
                    value={revenue.potrntialNetMargin}
                    Icon={WsIconChartLine}
                    heading="Potential Net Margin"
                    currency={revenue.clientBillRateCurrency ?? ''}
                    timeSpan={resolveUnitTime(revenue)}
                  />
                </Grid>
              )}
            </>
          )}
          {revenue.grossRevenue && (
            <Grid item>
              <CalculationUnitCard
                value={revenue.grossRevenue}
                Icon={WsIconCoin}
                heading="Gross Revenue"
                currency={revenue.salaryCurrency || revenue.clientBillRateCurrency || ''}
                timeSpan={
                  revenue.grossRevenueTimeSpan &&
                  revenue.standardContract &&
                  revenue.standardContract !== '4'
                    ? `${revenue.standardContract} Months`
                    : ''
                }
              />
            </Grid>
          )}
          {revenue.netRevenue && (
            <Grid item>
              <CalculationUnitCard
                value={revenue.netRevenue}
                Icon={WsIconCalculator}
                heading="Net Revenue"
                currency={revenue.salaryCurrency || revenue.clientBillRateCurrency || ''}
                timeSpan={
                  revenue.netFeeIncomeTimeSpan &&
                  revenue.standardContract &&
                  revenue.standardContract !== '4'
                    ? `${revenue.standardContract} Months`
                    : ''
                }
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PotentialRevenue;
